import { useCallback, useEffect, useEffectEvent, useMemo } from "react";
import LogsTab, { LogsTabItem } from "./LogsTab/LogsTab";
import CommentsTab, { CommentsTabItem } from "./CommentsTab/CommentsTab";
import FixaTabs, {
  FixaTab,
  FixaTabPanel,
} from "@coworker/apprestructured/src/shared/wrappers/FixaTabs/FixaTabs";
import { useTranslation } from "@coworker/locales";
import styles from "./LogsAndComments.module.css";
import { AddComment } from "../AddComment/AddComment";

interface LogsAndCommentsProps {
  onTabChanged: (tabId: Tab) => void;
  logsTabItems: LogsTabItem[];
  commentsTabItems: CommentsTabItem[];
  addCommentText: string;
  addCommentImages: File[];
  disableAllAddCommentInputs: boolean;
  disableAddCommentButton: boolean;
  onAddCommentTextChange: (text: string) => void;
  onAddCommentImagesChange: (images: File[]) => void;
  onAddCommentClick: () => void;
}

export type Tab = "logs" | "comments";

export const DEFAULT_TAB: Tab = "logs";

export const LogsAndComments = ({
  onTabChanged,
  logsTabItems,
  commentsTabItems,
  addCommentText,
  addCommentImages,
  disableAllAddCommentInputs,
  disableAddCommentButton,
  onAddCommentTextChange,
  onAddCommentImagesChange,
  onAddCommentClick,
}: LogsAndCommentsProps) => {
  const { t } = useTranslation();

  const overflowCarouselProps = useMemo(() => {
    return { className: styles["overflow"]! };
  }, []);

  const handleTabChange = useCallback(
    (tabPanelId: string) => {
      onTabChanged(tabPanelId as Tab);
    },
    [onTabChanged]
  );

  const handleUnmount = useEffectEvent(() => {
    onTabChanged(DEFAULT_TAB);
  });

  // This is a complex side effect due to the fact that the active tab is
  // uncontrolled, and the value resets on every mount. Thank you Skapa.
  useEffect(() => {
    return handleUnmount;
  }, []);

  const commentsTab = useMemo(() => {
    return <CommentsTab items={commentsTabItems} />;
  }, [commentsTabItems]);

  const logsTab = useMemo(() => {
    return <LogsTab items={logsTabItems} />;
  }, [logsTabItems]);

  return (
    <FixaTabs
      className={styles["root"]!}
      defaultActiveTab={DEFAULT_TAB}
      overflowCarouselProps={overflowCarouselProps}
      tabs={[
        <FixaTab key="logs" tabPanelId="logs" text={t("historyString")} />,
        <FixaTab
          key="comments"
          tabPanelId="comments"
          text={t("commentsCountString", { count: commentsTabItems.length })}
        />,
      ]}
      onTabChanged={handleTabChange}
      tabPanels={[
        <FixaTabPanel
          key="logs-tab-panel"
          tabPanelId="logs"
          className={styles["tabPanel"]!}
        >
          {logsTab}
        </FixaTabPanel>,
        <FixaTabPanel
          key="comments-tab-panel"
          tabPanelId="comments"
          className={styles["tabPanel"]!}
        >
          {commentsTab}
          <AddComment
            text={addCommentText}
            images={addCommentImages}
            disableAllInputs={disableAllAddCommentInputs}
            disableAddButton={disableAddCommentButton}
            onTextChange={onAddCommentTextChange}
            onImagesChange={onAddCommentImagesChange}
            onAddClick={onAddCommentClick}
          />
        </FixaTabPanel>,
      ]}
    />
  );
};
